import React from 'react'
import { Observable } from 'rx'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import { observeImmutable } from '@core/Components/HOCs'
import type { Campaign } from '@mobi/account/Areas/SignUp'
import { BonusBetButtonStyled } from './BonusBetButton.styles'

const isBonusCampaign = (campaign: Campaign) =>
  campaign.rewardType === 'BonusBetReward' || campaign.rewardType === 'BonusCashReward'

const state$ = Observable.combineLatest(
  userAccountState$,
  quickbetState$,
  (userAccountState, quickbetState): BonusBetButtonStateProps => {
    return {
      isLoggedIn: userAccountState.isLoggedIn ?? false,
      isUsingBonusBet: quickbetState.isUsingBonusBet,
      isBusy: quickbetState.isBusy,
      canConfirmBet: quickbetState.canConfirmBet,
      hasNotification: !!quickbetState.notificationType,
      activeCampaigns: userAccountState.activeCampaigns?.filter(isBonusCampaign).toArray(),
      insufficientFundsForBet: quickbetState.insufficientFundsForBet,
    }
  }
)

export type BonusBetButtonStateProps = {
  isLoggedIn: boolean
  isUsingBonusBet: boolean
  isBusy: boolean
  canConfirmBet: boolean
  hasNotification: boolean
  activeCampaigns: Campaign[] | undefined
  insufficientFundsForBet: boolean
}

export type BonusBetExternalProps = {
  toggleBonusBet: () => void
  trackBonusBetClick: () => void
}

export const BonusBetButtonComponent = ({
  isUsingBonusBet,
  toggleBonusBet,
  trackBonusBetClick,
  canConfirmBet,
  hasNotification,
  isBusy,
  isLoggedIn,
  activeCampaigns,
  insufficientFundsForBet,
}: BonusBetButtonStateProps & BonusBetExternalProps): JSX.Element | null => {
  const hasActiveCampaigns = !!(activeCampaigns?.length ?? 0)

  const shouldShowBonusBetButton =
    !insufficientFundsForBet &&
    isLoggedIn &&
    hasActiveCampaigns &&
    !canConfirmBet &&
    !hasNotification

  if (!shouldShowBonusBetButton) {
    return null
  }

  return (
    <BonusBetButtonStyled
      id='quickbet-bonusbet-button'
      onClick={() => {
        toggleBonusBet()
        trackBonusBetClick()
      }}
      selected={isUsingBonusBet}
      disabled={isBusy}
    >
      {isUsingBonusBet ? 'Use Balance' : 'Use My Bonus'}
    </BonusBetButtonStyled>
  )
}

export const BonusBetButton = observeImmutable<BonusBetButtonStateProps, BonusBetExternalProps>(
  state$,
  ({ record, toggleBonusBet, trackBonusBetClick }) => {
    return (
      <BonusBetButtonComponent
        {...record}
        toggleBonusBet={toggleBonusBet}
        trackBonusBetClick={trackBonusBetClick}
      />
    )
  }
)
